import type { ExchangeResolution } from './exchange'
import { roundCurrency, sumTicketTaxes } from './money'
import type { BookingFinancialInputs, RefundQuoteData, RefundQuoteTicket } from './types'
import { classifyTicketsByPaxType } from './utils'

/**
 * NettPaidToSupplier resolution: what PLT paid the supplier for the selected tickets,
 * gross of taxes, with the winning basis recorded so downstream can judge its fidelity.
 */

export type NettPaidBasis =
  | 'PD_NET_FARE_PLUS_CHANGE'
  | 'TICKET_PAYMENTS'
  | 'GDS_FARE_PLUS_TAXES'
  | 'PD_NET_FARE_COST'
  | 'GDS_FARE_ONLY'
  | 'TTV_ESTIMATE'

export interface NettPaidResolution {
  total: number
  /** Per selected ticket; empty for booking-level bases (PD_NET_FARE_COST, TTV_ESTIMATE). */
  perTicket: Record<string, number>
  basis: NettPaidBasis
  warnings: string[]
  /** Whole-booking untied fees included in `total` (TICKET_PAYMENTS on a full selection only). */
  untiedFees?: number
}

export interface ResolveNettPaidParams {
  refundQuoteData: RefundQuoteData
  booking: BookingFinancialInputs
  selectedTicketNumbers: Set<string>
  currency: string
  exchange?: ExchangeResolution
}

export function resolveNettPaidToSupplier(p: ResolveNettPaidParams): NettPaidResolution {
  const { refundQuoteData: rqd, booking } = p
  const tickets = rqd.tickets ?? []
  const selected = [...p.selectedTicketNumbers]
  const fxRate = rqd.fareToBookingCurrencyRate ?? 1
  const nonInfants = (booking.travellersTotal ?? 0) - (parseInt(booking.infantsTotal ?? '0') || 0)

  // 0. A changed booking: its ticket payments and GDS fare and taxes span both issues, so the cost is
  // what PLT paid for the original tickets plus the change, shared per passenger (SPEC.md §2a).
  if (p.exchange?.exchanged && (booking.netFareCost ?? 0) > 0 && selected.length > 0) {
    const totalPax = nonInfants > 0 ? nonInfants : tickets.length || 1
    const perPax = roundCurrency((booking.netFareCost! + p.exchange.supplierChangeFee) / totalPax)
    return {
      total: roundCurrency(perPax * selected.length),
      perTicket: Object.fromEntries(selected.map(n => [n, perPax])),
      basis: 'PD_NET_FARE_PLUS_CHANGE',
      warnings: [],
    }
  }

  // 1. Per-ticket supplier payments — exact, gross of taxes.
  const payments = rqd.ticketPayments
  if (selected.length > 0 && payments && selected.every(n => (payments[n]?.total ?? 0) > 0)) {
    const perTicket = Object.fromEntries(selected.map(n => [n, roundCurrency(payments[n].total)]))
    const ticketTotal = selected.reduce((sum, n) => sum + payments[n].total, 0)
    const warnings: string[] = []

    // Untied whole-booking fees (PROCFE/SUPPLY-style DRA lines with no ticket number) can't be
    // prorated to a partial selection — only add them when cancelling every ticket on the booking.
    const isFullSelection = selected.length === tickets.length
    const untiedFees = rqd.untiedBookingFees ?? 0
    if (untiedFees > 0 && !isFullSelection) {
      warnings.push(
        `untied-fees-partial-selection: whole-PNR untied booking fees (${untiedFees}) cannot be prorated to a ` +
          `partial selection`,
      )
    }

    return {
      total: roundCurrency(ticketTotal + (isFullSelection ? untiedFees : 0)),
      perTicket,
      basis: 'TICKET_PAYMENTS',
      warnings,
      untiedFees: isFullSelection ? untiedFees : 0,
    }
  }

  // 2. GDS base fare + the ticket's own taxes — requires a tax breakdown for every selected ticket.
  const fares = perTicketGdsFares(rqd, selected, tickets, fxRate)
  if (fares) {
    const allHaveTaxes = selected.every(n => (rqd.allTaxesByTicket?.[n]?.length ?? 0) > 0)

    if (allHaveTaxes) {
      let total = 0
      const perTicket: Record<string, number> = {}

      for (const n of selected) {
        const gross = roundCurrency(fares[n] + sumTicketTaxes([n], rqd.allTaxesByTicket, p.currency, fxRate))
        perTicket[n] = gross
        total += gross
      }

      return { total: roundCurrency(total), perTicket, basis: 'GDS_FARE_PLUS_TAXES', warnings: [] }
    }
  }

  // 3. Pipedrive netFareCost — booking-level (includes commission/issuing fees); pro-rated per pax.
  if (booking.netFareCost != null && booking.netFareCost > 0) {
    const totalPax = nonInfants > 0 ? nonInfants : tickets.length || 1
    const share = selected.length > 0 && tickets.length > 0 ? Math.min(1, selected.length / totalPax) : 1
    return {
      total: roundCurrency(booking.netFareCost * share),
      perTicket: {},
      basis: 'PD_NET_FARE_COST',
      warnings:
        share < 1
          ? [
              `nett-paid-pro-rated: Pipedrive's booking-level net (${booking.netFareCost}) split ${selected.length} of ` +
                `${totalPax} passengers`,
            ]
          : [],
    }
  }

  // 4. GDS base fare with no usable tax breakdown — gross basis unknowable, flag it.
  if (fares) {
    const perTicket = Object.fromEntries(selected.map(n => [n, roundCurrency(fares[n])]))
    return {
      total: roundCurrency(selected.reduce((sum, n) => sum + fares[n], 0)),
      perTicket,
      basis: 'GDS_FARE_ONLY',
      warnings: ['nett-paid-taxes-unknown: no tax breakdown for the selection — NettPaid is fare-only'],
    }
  }
  if (selected.length === 0 && rqd.netFarePerAdt !== undefined) {
    const adt = rqd.paxAdtCount ?? 1
    const chd = rqd.paxChdCount ?? 0
    const chdFare = (rqd.netFarePerChd ?? 0) > 0 ? rqd.netFarePerChd! : rqd.netFarePerAdt
    return {
      total: roundCurrency((rqd.netFarePerAdt * adt + chdFare * chd) * fxRate),
      perTicket: {},
      basis: 'GDS_FARE_ONLY',
      warnings: ['nett-paid-taxes-unknown: booking-level GDS fare with no tax breakdown'],
    }
  }

  // 5. Sell-price estimate — nett unknown, assume zero margin. Last resort, loudly flagged.
  return {
    total: roundCurrency(booking.value ?? 0),
    perTicket: {},
    basis: 'TTV_ESTIMATE',
    warnings: ['nett-paid-unknown: falling back to the sell price — margin is invisible to this quote'],
  }
}

function perTicketGdsFares(
  rqd: RefundQuoteData,
  selected: string[],
  tickets: RefundQuoteTicket[],
  fxRate: number,
): Record<string, number> | null {
  if (selected.length === 0 || rqd.netFarePerAdt === undefined) return null

  const { adultNums, childNums } = classifyTicketsByPaxType(
    tickets,
    rqd.paxAdtCount ?? selected.length,
    rqd.paxChdCount ?? 0,
  )
  const adtFare = rqd.netFarePerAdt * fxRate
  const chdFare = (rqd.netFarePerChd ?? 0) > 0 ? rqd.netFarePerChd! * fxRate : adtFare
  const infFare = (rqd.netFarePerInf ?? 0) * fxRate

  return Object.fromEntries(selected.map(n => [n, childNums.has(n) ? chdFare : adultNums.has(n) ? adtFare : infFare]))
}

/**
 * TotalPaidByPax for the selection: the full payment for a full cancellation,
 * a pro-rata share (by nett ticket value, falling back to pax count) for a partial one.
 */
export interface PaxGrossAllocation {
  totalPaidForSelection: number
  /** Fraction of the booking's payment attributed to the selection (1 for full cancellations). */
  share: number
}

export interface ResolvePaxGrossParams {
  totalPaid: number
  isFullSelection: boolean
  selectedNett: number
  /** Nett value of ALL tickets on the booking, when computable — drives the pro-rata share. */
  allTicketsNett?: number
  cancellingPax: number
  totalPax: number
}

export function resolvePaxGrossAllocation(p: ResolvePaxGrossParams): PaxGrossAllocation {
  if (p.isFullSelection) return { totalPaidForSelection: roundCurrency(p.totalPaid), share: 1 }

  const share =
    p.allTicketsNett != null && p.allTicketsNett > 0
      ? Math.min(1, p.selectedNett / p.allTicketsNett)
      : p.totalPax > 0
        ? Math.min(1, p.cancellingPax / p.totalPax)
        : 1

  return { totalPaidForSelection: roundCurrency(p.totalPaid * share), share }
}
